import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export const UserDialog = ({ user, partners, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", company: user?.company || "", password: "",
    role: user?.role || "user", partner_id: user?.partner_id || "genis",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, partner_id: form.partner_id === "genis" ? null : form.partner_id, password: form.password || null };
      if (user) await api.put(`/admin/users/${user.id}`, body);
      else await api.post("/admin/users", body);
      toast.success(user ? "Compte mis à jour" : "Compte créé");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader><DialogTitle>{user ? "Modifier le compte" : "Créer un compte"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2"><Label>Nom *</Label><Input value={form.name} onChange={set("name")} data-testid="user-name-input" /></div>
          <div className="space-y-1.5 col-span-2"><Label>Email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="user-email-input" /></div>
          <div className="space-y-1.5"><Label>Société</Label><Input value={form.company} onChange={set("company")} data-testid="user-company-input" /></div>
          <div className="space-y-1.5"><Label>{user ? "Nouveau mot de passe" : "Mot de passe *"}</Label><Input type="text" value={form.password} onChange={set("password")} placeholder={user ? "Inchangé si vide" : "6 caractères min."} data-testid="user-password-input" /></div>
          <div className="space-y-1.5">
            <Label>Espace</Label>
            <Select value={form.partner_id} onValueChange={(v) => setForm({ ...form, partner_id: v, role: v === "genis" ? form.role : "user" })}>
              <SelectTrigger data-testid="user-partner-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="genis">Genis Energie</SelectItem>
                {partners.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`user-partner-option-${p.slug}`}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={form.partner_id !== "genis"}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">{form.partner_id !== "genis" ? "Partenaire" : "Utilisateur"}</SelectItem>
                <SelectItem value="admin">Administrateur</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-genis hover:bg-genis-dark text-white" data-testid="user-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
